import { Loading, Modal } from '@carbon/react';
import { Add, ShoppingCart } from '@carbon/icons-react';
import {
    IconCalendar,
    IconEdit,
    IconExternalLink,
    IconEye,
    IconTag,
    IconTrash,
    IconUsersGroup,
} from '@tabler/icons-react';
import {
    ActionIcon,
    Anchor,
    Badge,
    Group,
    Paper,
    Text,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import { useDeleteOfferMutation, useGetOffersQuery } from 'api/offerEndpoints';
import type { OfferDto } from 'api/dtos/offer';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import InfoCard from 'Components/Common/InfoCard/InfoCard';
import TableSearchInput from 'Components/Common/TableSearchInput/TableSearchInput';
import sharedMessages from 'Components/utils/sharedMessages';
import AddOfferView from '../AddOfferView/AddOfferView';
import EditOfferView from '../EditOfferView/EditOfferView';

import styles from './OffersView.module.scss';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import useGetUser from 'hooks/useGetUser';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const getOfferImageSrc = (offer: OfferDto) => {
    if (!offer.imageData) {
        return offer.imageUrl;
    }

    const extension = offer.imageFileName.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        bmp: 'image/bmp',
    };
    const mimeType = extension ? mimeTypes[extension] ?? 'image/jpeg' : 'image/jpeg';

    return `data:${mimeType};base64,${offer.imageData}`;
};

const isVisibleForAllSubgroups = (offer: OfferDto): boolean => {
    if (typeof offer.applyToChildren === 'boolean') return Boolean(offer.applyToChildren);
    return true;
};

const OffersView = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const hasPermission = isAdmin || isModerator || isOwner;
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasSubscriptionAccess = !isFetchingSubscription && isPaidPlan && activeSubscription?.hasAccess !== false;
    const isTopBarActionDisabled = !hasPermission || !hasSubscriptionAccess;
    const {
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    } = useMainContainer();

    const [search, setSearch] = useState('');
    const [createOfferOpen, setCreateOfferOpen] = useState(false);
    const [offerToEdit, setOfferToEdit] = useState<OfferDto | null>(null);
    const [offerToDelete, setOfferToDelete] = useState<OfferDto | null>(null);

    const { data, isFetching, isError, refetch } = useGetOffersQuery(currentEntityId ?? '', {
        skip: !currentEntityId,
    });
    const [deleteOffer, { isLoading: isDeleting }] = useDeleteOfferMutation();

    useEffect(() => {
        setTopBarAction(() => () => setCreateOfferOpen(true));
        setTopBarActionTitle(intl.formatMessage(messages.add));
        setTopBarIcon(<Add size={20} />);
        setDisabledAction(isTopBarActionDisabled);
        setDisabledActionTooltip(intl.formatMessage(messages.addDisabledTooltip));

        return () => {
            setTopBarAction(null);
            setTopBarActionTitle(undefined);
            setTopBarIcon(undefined);
            setDisabledAction(false);
            setDisabledActionTooltip(undefined);
        };
    }, [
        intl,
        isTopBarActionDisabled,
        setDisabledAction,
        setDisabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
    ]);

    const offers = useMemo(() => data ?? [], [data]);

    const filteredOffers = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return offers;

        return offers.filter((offer) =>
            offer.name.toLowerCase().includes(term) ||
            offer.companyName.toLowerCase().includes(term) ||
            offer.description.toLowerCase().includes(term));
    }, [offers, search]);
    const showEmptyState = filteredOffers.length === 0 && !isFetching;

    const formatDate = (iso?: string) => {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const formatDateBadge = (iso?: string) => {
        if (!iso) return null;
        const date = new Date(iso);
        const month = date.toLocaleDateString('pl-PL', { month: 'long' }).toUpperCase();

        return {
            day: date.toLocaleDateString('pl-PL', { day: 'numeric' }),
            month,
            year: date.toLocaleDateString('pl-PL', { year: 'numeric' }),
        };
    };

    const formatPrice = (value: number) =>
        new Intl.NumberFormat('pl-PL', {
            style: 'currency',
            currency: 'PLN',
        }).format(value);

    const handleDeleteOffer = async () => {
        if (!currentEntityId || !offerToDelete) return;

        await deleteOffer({
            entityId: currentEntityId,
            id: offerToDelete.id,
        }).unwrap();
        setOfferToDelete(null);
    };

    if (isError) {
        return <ErrorActionState className={styles.error} onClick={refetch} />;
    }

    return (
        <>
            {(isFetching || isDeleting) && <Loading />}
            <div className={`${styles.container} ${showEmptyState ? styles.emptyContainer : ''}`}>
                <InfoCard
                    id="offers-info"
                    className={styles.infoCard}
                    isHorizontal
                    isClosable
                    illustration={<ShoppingCart />}
                    title={intl.formatMessage(messages.infoCardTitle)}
                    description={intl.formatMessage(messages.infoCardDescription)}
                />
                {(offers.length > 0 || search) && (
                    <TableSearchInput
                        className={styles.search}
                        id="search-offers"
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={search}
                        onChange={setSearch}
                    />
                )}
                {showEmptyState ? (
                    <EmptyState title={intl.formatMessage(messages.empty)} />
                ) : (
                    <Paper className={styles.list} radius="md" withBorder>
                        {filteredOffers.map((offer) => {
                            const visibleForAllSubgroups = isVisibleForAllSubgroups(offer);
                            const dateBadge = formatDateBadge(offer.startAt ?? offer.createdAt);
                            const imageSrc = getOfferImageSrc(offer);
                            const visibilityIcon = visibleForAllSubgroups
                                ? <IconEye size={18} />
                                : <IconUsersGroup size={18} />;
                            const visibilityMessage = visibleForAllSubgroups
                                ? messages.visibleForAllSubgroups
                                : messages.visibleForGroup;

                            return (
                                <div
                                    key={offer.id}
                                    className={`${styles.row} ${imageSrc ? styles.withImage : styles.withoutImage}`}
                                >
                                    <div className={styles.dateCell}>
                                        {dateBadge && (
                                            <>
                                                <span className={styles.day}>{dateBadge.day}</span>
                                                <span className={styles.month}>{dateBadge.month}</span>
                                                <span className={styles.year}>{dateBadge.year}</span>
                                            </>
                                        )}
                                    </div>
                                    {imageSrc && (
                                        <img
                                            className={styles.image}
                                            src={imageSrc}
                                            alt=""
                                        />
                                    )}
                                    <div className={styles.details}>
                                        <Group gap="xs" wrap="nowrap" className={styles.titleRow}>
                                            <Text className={styles.title}>{offer.name}</Text>
                                            <span className={styles.company}>{offer.companyName}</span>
                                        </Group>
                                        <Text className={styles.description}>
                                            {offer.description}
                                        </Text>
                                        <Group gap="xs" className={styles.meta}>
                                            <span className={styles.metaItem}>
                                                <IconTag size={15} />
                                                {formatPrice(offer.discountedPrice)}
                                                {offer.originalPrice !== offer.discountedPrice && (
                                                    <span className={styles.oldPrice}>
                                                        {formatPrice(offer.originalPrice)}
                                                    </span>
                                                )}
                                            </span>
                                            {(offer.startAt || offer.endAt) && (
                                                <span className={styles.metaItem}>
                                                    <IconCalendar size={15} />
                                                    {formatDate(offer.startAt)}
                                                    {offer.endAt ? ` - ${formatDate(offer.endAt)}` : ''}
                                                </span>
                                            )}
                                            {offer.offerUrl && (
                                                <Anchor
                                                    className={styles.offerLink}
                                                    href={offer.offerUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <IconExternalLink size={15} />
                                                    {intl.formatMessage(messages.visitOffer)}
                                                </Anchor>
                                            )}
                                        </Group>
                                    </div>
                                    <div className={styles.visibilityCell}>
                                        <Badge
                                            className={styles.visibilityBadge}
                                            color={visibleForAllSubgroups ? 'green' : 'gray'}
                                            variant="light"
                                            radius="sm"
                                            size="lg"
                                            leftSection={visibilityIcon}
                                        >
                                            {intl.formatMessage(visibilityMessage)}
                                        </Badge>
                                    </div>
                                    {hasPermission && (
                                        <Group gap="xs" className={styles.actions} wrap="nowrap">
                                            <ActionIcon
                                                className={styles.actionButton}
                                                variant="subtle"
                                                color="gray"
                                                size="xl"
                                                aria-label={intl.formatMessage(messages.edit)}
                                                onClick={() => setOfferToEdit(offer)}
                                            >
                                                <IconEdit size={22} />
                                            </ActionIcon>
                                            <ActionIcon
                                                className={styles.actionButton}
                                                variant="subtle"
                                                color="red"
                                                size="xl"
                                                aria-label={intl.formatMessage(messages.delete)}
                                                onClick={() => setOfferToDelete(offer)}
                                            >
                                                <IconTrash size={22} />
                                            </ActionIcon>
                                        </Group>
                                    )}
                                </div>
                            );
                        })}
                    </Paper>
                )}
            </div>
            <AddOfferView
                open={createOfferOpen}
                onClose={() => setCreateOfferOpen(false)}
            />
            <EditOfferView
                offer={offerToEdit}
                open={!!offerToEdit}
                onClose={() => setOfferToEdit(null)}
            />
            <Modal
                open={!!offerToDelete}
                onRequestClose={() => setOfferToDelete(null)}
                onRequestSubmit={handleDeleteOffer}
                danger
                modalHeading={intl.formatMessage(messages.deleteConfirm)}
                modalLabel={intl.formatMessage(sharedMessages.irreversibleAction)}
                primaryButtonText={intl.formatMessage(sharedMessages.remove)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            />
        </>
    );
};

const OffersViewStateHandler = () => {
    const currentEntityId = useGetCurrentEntityId();
    const intl = useIntl();

    const user = useGetUser();
            
    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    if (!currentEntityId) {
        return <EmptyState title={intl.formatMessage(sharedMessages.noEntitySelected)} />;
    }

    return <OffersView />;
};

const messages = defineMessages({
    add: {
        id: 'offers.add',
        defaultMessage: 'Dodaj ofertę',
    },
    addDisabledTooltip: {
        id: 'offers.addDisabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do dodania oferty.',
    },
    empty: {
        id: 'offers.empty',
        defaultMessage: 'Brak ofert. Dodaj pierwszą ofertę.',
    },
    searchPlaceholder: {
        id: 'offers.search',
        defaultMessage: 'Szukaj po nazwie, firmie lub opisie',
    },
    visitOffer: {
        id: 'offers.visitOffer',
        defaultMessage: 'Odwiedź ofertę',
    },
    visibleForAllSubgroups: {
        id: 'offers.visibleForAllSubgroups',
        defaultMessage: 'Zastosowano do wszystkich podgrup',
    },
    visibleForGroup: {
        id: 'offers.visibleForGroup',
        defaultMessage: 'Widoczne dla tylko dla tej grupy',
    },
    edit: {
        id: 'offers.edit',
        defaultMessage: 'Edytuj ofertę',
    },
    delete: {
        id: 'offers.delete',
        defaultMessage: 'Usuń ofertę',
    },
    deleteConfirm: {
        id: 'offers.deleteConfirm',
        defaultMessage: 'Czy na pewno chcesz usunąć tę ofertę?',
    },
    infoCardTitle: {
        id: 'offers.infoCard.title',
        defaultMessage: 'Co to są oferty?',
    },
    infoCardDescription: {
        id: 'offers.infoCard.description',
        defaultMessage: 'To produkty lub usługi, które parafia udostępnia parafianom - np. wycieczki, pielgrzymki, książki, płyty, świece, kalendarze i inne. Parafianie mogą je przeglądać i kupować w aplikacji mobilnej.',
    },
});

export default OffersViewStateHandler;
